/**
 * @fileoverview Utility Context for providing common utility functions and user settings.
 *
 * This context provides essential utility functionality including:
 * - Currency formatting with negative value handling
 * - Date formatting with customizable long/short formats
 * - Array sum calculation utility
 * - User settings management (formatted dates, notification modes)
 * - Persistent settings storage in localStorage
 *
 * Used throughout the application for:
 * - Consistent currency and date formatting
 * - Mathematical operations on arrays
 * - User preference management
 * - Data display formatting
 * - Settings persistence across sessions
 */

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { FiCheck } from 'react-icons/fi';
import '../styles/LoginTransition.css';

/**
 * React Context for utility functions and settings
 * @type {React.Context<UtilContextValue>}
 */
const UtilContext = createContext();

/**
 * Custom hook to access the utility context.
 *
 * @function useUtils
 * @returns {UtilContextValue} The utility context value containing utility functions and settings
 *
 * @example
 * ```jsx
 * function InvoiceItem({ invoice }) {
 *   const { formatCurrency, formatDate, settingsObj } = useUtils();
 *
 *   return (
 *     <div>
 *       <p>Amount: {formatCurrency(invoice.amount)}</p>
 *       <p>Date: {formatDate(invoice.date, settingsObj.isFormattedDatesEnabled)}</p>
 *     </div>
 *   );
 * }
 * ```
 */
export const useUtils = () => {
  return useContext(UtilContext);
};

/**
 * Utilities Provider component that provides utility functions and manages user settings.
 *
 * @component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components that will have access to utility context
 * @returns {JSX.Element} Provider component wrapping children with utility context
 *
 * @example
 * ```jsx
 * function App() {
 *   return (
 *     <UtilsProvider>
 *       <Router>
 *         <Routes>
 *           <Route path="/dashboard" element={<Dashboard />} />
 *         </Routes>
 *       </Router>
 *     </UtilsProvider>
 *   );
 * }
 * ```
 */
export const UtilsProvider = ({ children }) => {
  // Get initial values from localStorage
  const initialFormattedDates = localStorage.getItem('formattedDates') === 'true';
  const initialNotifsMode = localStorage.getItem('notifsMode') || 'custom';
  const initialExpandedCalendar = localStorage.getItem('expandedCalendarDefault') === 'true';

  // Set up state with initial values
  const [isFormattedDatesEnabled, setIsFormattedDatesEnabled] = useState(initialFormattedDates);
  const [notifsMode, setNotifsMode] = useState(initialNotifsMode);
  const [expandedCalendarDefault, setExpandedCalendarDefault] = useState(initialExpandedCalendar);

  // Update localStorage when settings change
  useEffect(() => {
    localStorage.setItem('formattedDates', isFormattedDatesEnabled);
  }, [isFormattedDatesEnabled]);

  useEffect(() => {
    localStorage.setItem('notifsMode', notifsMode);
  }, [notifsMode]);

  useEffect(() => {
    localStorage.setItem('expandedCalendarDefault', expandedCalendarDefault);
  }, [expandedCalendarDefault]);
  
  /**
   * Formats a numeric amount as currency with negative value handling
   * @param {number|string|null|undefined} amount - The amount to format
   * @param {boolean} [showCents=false] - Whether to show cents (default: false, rounds down to whole dollars)
   * @returns {string} Formatted currency string (negative values shown in brackets)
   *
   * @example
   * ```jsx
   * formatCurrency(1250.75) // "$1,250" (rounded down)
   * formatCurrency(1250.75, true) // "$1,250.75" (with cents)
   * formatCurrency(-500) // "[$500]"
   * formatCurrency(null) // "$0"
   * ```
   */
  const formatCurrency = (amount, showCents = false) => {
    if (amount === undefined || amount === null) return '$0';
    const isNeg = amount < 0
    if(isNeg) amount = amount * -1

    // Round down to whole dollars unless showCents is true
    const roundedAmount = showCents ? amount : Math.floor(amount);

    const formattedNum = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: showCents ? 2 : 0,
      maximumFractionDigits: showCents ? 2 : 0,
    }).format(roundedAmount);

    return isNeg ? `[${formattedNum}]` : formattedNum
  };

  /**
   * Formats a date string with optional long format
   * @param {string} dateStr - Date string in YYYY-MM-DD format
   * @param {boolean} [long] - Whether to use long format (overrides user setting)
   * @returns {string} Formatted date string
   *
   * @example
   * ```jsx
   * formatDate('2023-12-25') // "12/25/2023" or "December 25, 2023" (based on user setting)
   * formatDate('2023-12-25', true) // "December 25, 2023" (force long format)
   * formatDate('') // ""
   * ```
   */
  const formatDate = (dateStr, long) => {
    if (!dateStr) return '';

    // Parse the date to ensure it is treated as a local date
    const [year, month, day] = dateStr.split(' ')[0].split('-').map(Number);
    const date = new Date(year, month - 1, day); // Month is 0-based in JavaScript
    let options = { year: 'numeric', month: 'numeric', day: 'numeric' };

    // Handle long dates if long dates enabled in settings or was passed in to func
    if (isFormattedDatesEnabled || long) {
      options.month = 'long';
    }

    return date.toLocaleDateString(undefined, options);
  };

  /**
   * Calculates the sum of an array with optional key extraction
   * @param {Array} iter - Array to sum
   * @param {string} [key] - Object key to extract values from (for array of objects)
   * @param {number} [initial=0] - Initial value for the sum
   * @returns {number} Sum of array values
   *
   * @example
   * ```jsx
   * sum([1, 2, 3]) // 6
   * sum([{amount: 10}, {amount: 20}], 'amount') // 30
   * sum([1, 2], null, 100) // 103
   * sum(null) // 0
   * ```
   */
  const sum = (iter, key, initial = 0) => {
    return iter ? iter.reduce((accumulator, currentValue) => {
      return accumulator + (key ? currentValue[key] : currentValue);
    }, initial) : initial;
  };
  
  /**
   * Settings object containing user preferences and their setters
   * @typedef {Object} SettingsObject
   * @property {boolean} isFormattedDatesEnabled - Whether long date format is enabled
   * @property {Function} setIsFormattedDatesEnabled - Setter for date format preference
   * @property {string} notifsMode - Current notification mode ('custom', 'notistack', 'notiflix')
   * @property {Function} setNotifsMode - Setter for notification mode
   * @property {boolean} expandedCalendarDefault - Whether to open calendar in expanded/fullscreen mode by default
   * @property {Function} setExpandedCalendarDefault - Setter for expanded calendar preference
   */
  const settingsObj = {
    isFormattedDatesEnabled,
    setIsFormattedDatesEnabled,
    notifsMode,
    setNotifsMode,
    expandedCalendarDefault,
    setExpandedCalendarDefault
  };

  // Login transition overlay state
  const [loginTransitionVisible, setLoginTransitionVisible] = useState(false);
  const [loginTransitionFading, setLoginTransitionFading] = useState(false);
  const [loginTransitionMessage, setLoginTransitionMessage] = useState('Welcome back!');
  const [loginTransitionSubMessage, setLoginTransitionSubMessage] = useState('Signing you in...');

  /**
   * Show the login success overlay transition
   * @param {Object} options - Display options
   * @param {string} options.message - Main message (default: "Welcome back!")
   * @param {string} options.subMessage - Sub message (default: "Signing you in...")
   * @param {number} options.displayDuration - How long to show before fading (default: 1200ms)
   * @param {number} options.fadeDuration - How long the fade animation takes (default: 400ms)
   * @param {Function} options.onFadeStart - Callback when fade starts (navigate here!)
   * @param {Function} options.onComplete - Callback when animation completes
   */
  const showLoginTransition = useCallback(async ({
    message = 'Welcome back!',
    subMessage = 'Signing you in...',
    displayDuration = 1200,
    fadeDuration = 400,
    onFadeStart,
    onComplete
  } = {}) => {
    setLoginTransitionMessage(message);
    setLoginTransitionSubMessage(subMessage);
    setLoginTransitionVisible(true);
    setLoginTransitionFading(false);

    // Wait for display duration
    await new Promise(resolve => setTimeout(resolve, displayDuration));

    // Start fade - call onFadeStart so navigation happens NOW (dashboard behind overlay)
    setLoginTransitionFading(true);
    if (onFadeStart) {
      onFadeStart();
    }

    // Wait for fade to complete
    await new Promise(resolve => setTimeout(resolve, fadeDuration));

    // Hide and cleanup
    setLoginTransitionVisible(false);
    setLoginTransitionFading(false);

    if (onComplete) {
      onComplete();
    }
  }, []);

  const hideLoginTransition = useCallback(() => {
    setLoginTransitionVisible(false);
    setLoginTransitionFading(false);
  }, []);

  /**
   * The context value object containing all utility functions and settings
   * @typedef {Object} UtilContextValue
   * @property {Function} formatCurrency - Format numeric amounts as currency
   * @property {Function} formatDate - Format date strings with optional long format
   * @property {Function} sum - Calculate sum of array with optional key extraction
   * @property {SettingsObject} settingsObj - User settings and their setters
   */
  return (
    <UtilContext.Provider value={{ formatCurrency, formatDate, sum, settingsObj, showLoginTransition, hideLoginTransition }}>
      {children}

      {/* Global login transition overlay - persists during navigation */}
      {loginTransitionVisible && (
        <div className={`login-transition-overlay ${loginTransitionFading ? 'fade-out' : ''}`}>
          <div className="login-transition-content">
            <FiCheck className="login-transition-icon" />
            <h2 className="login-transition-title">{loginTransitionMessage}</h2>
            <p className="login-transition-message">{loginTransitionSubMessage}</p>
          </div>
        </div>
      )}
    </UtilContext.Provider>
  );
};